import { useParams, Link } from 'react-router';
import { ArrowLeft, MapPin, Map, Grid3x3, Gauge } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Game } from '../types/curling';
import { getGamesForSheet } from '../utils/storage';

export function ClubSheetGames() {
  const { club, sheet } = useParams<{ club: string; sheet: string }>();
  const [games, setGames] = useState<Game[]>([]);
  
  useEffect(() => {
    let cancelled = false;

    // Filtered server-side now, rather than fetching every game and narrowing here.
    getGamesForSheet(decodeURIComponent(club || ''), sheet || '')
      .then(found => {
        if (cancelled) return;
        setGames([...found].sort((a, b) => b.date - a.date)); // Newest first
      })
      .catch(error => console.error('Failed to load games for sheet:', error));

    return () => {
      cancelled = true;
    };
  }, [club, sheet]);

  const decodedClub = club ? decodeURIComponent(club) : '';

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/CurlingCodex"
            className="inline-flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
              <div className="flex items-center gap-3">
                <MapPin className="w-6 h-6 text-blue-300" />
                <h1 className="text-2xl font-bold text-white">{decodedClub}</h1>
              </div>
              {games.length > 0 && (
                <Link
                  to={`/CurlingCodex/rock-map/${encodeURIComponent(decodedClub)}/${sheet}`}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <Map className="w-4 h-4" />
                  Rock Map for this sheet
                </Link>
              )}
            </div>
            <p className="text-blue-300 ml-9">
              Sheet {sheet} - {games.length} {games.length === 1 ? 'game' : 'games'} recorded
            </p>
          </div>
        </div>

        {/* Games List */}
        {games.length === 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <Grid3x3 className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <p className="text-blue-300 text-lg mb-2">No games found</p>
            <p className="text-slate-400 text-sm">
              There are no games recorded for {decodedClub} - Sheet {sheet}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {games.map(game => (
              <div
                key={game.id}
                className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 hover:bg-white/12 transition-colors p-6"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Game Info */}
                  <div className="flex-1">
                    <div className="text-sm text-blue-300 mb-2">
                      {formatDate(game.date)}
                    </div>

                    <div className="space-y-1 mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-lg text-white">{game.team1Name}</span>
                        <span className="text-2xl font-bold text-blue-300">
                          {game.team1TotalScore}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-lg text-white">{game.team2Name}</span>
                        <span className="text-2xl font-bold text-blue-300">
                          {game.team2TotalScore}
                        </span>
                      </div>
                    </div>

                    {game.isComplete && (
                      <span className="inline-block px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded">
                        Complete
                      </span>
                    )}
                    {!game.isComplete && (
                      <span className="inline-block px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs rounded">
                        In Progress
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    <Link
                      to={`/CurlingCodex/game/${game.id}/sheet-map`}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      <Grid3x3 className="w-4 h-4" />
                      Sheet Map
                    </Link>

                    <Link
                      to={`/CurlingCodex/game/${game.id}/rock-map`}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      <Map className="w-4 h-4" />
                      Rock Map
                    </Link>

                    <Link
                      to={`/CurlingCodex/game/${game.id}/brc-scoring`}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      <Gauge className="w-4 h-4" />
                      BRC Scoring
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}